import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiBookOpen } = FiIcons;

const ModuleThree = ({ data, onSave, completed }) => {
  const [formData, setFormData] = useState({
    projectScope: 'new',
    targetWordCount: '',
    estimatedChapters: '',
    bookType: 'standalone',
    idealStartDate: '',
    idealFinishDate: '',
    constraints: '',
    sessionsPerWeek: '',
    wordsPerSession: '',
    preferredDays: [],
    bufferWeeks: '',
    flexibilityNotes: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...data }));
  }, [data]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      preferredDays: prev.preferredDays.includes(day)
        ? prev.preferredDays.filter(d => d !== day)
        : [...prev.preferredDays, day]
    }));
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    setTimeout(() => {
      onSave(formData, markComplete);
      setIsLoading(false);
    }, 500);
  };

  const canComplete = [
    'projectScope', 'targetWordCount', 'estimatedChapters', 'bookType',
    'idealStartDate', 'idealFinishDate', 'sessionsPerWeek', 'wordsPerSession'
  ].every(field => formData[field] && formData[field].toString().trim() !== '');

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <ModuleLayout
      title="Build Your Book Plan"
      description="Turn vision into a realistic, grace-filled plan that fits real life."
      icon={FiBookOpen}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Project Scope */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Scope</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Status
              </label>
              <select
                value={formData.projectScope}
                onChange={(e) => handleInputChange('projectScope', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="new">New Project</option>
                <option value="restart">Restart Existing</option>
                <option value="finish">Finish Current</option>
              </select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Word Count
                </label>
                <input
                  type="number"
                  value={formData.targetWordCount}
                  onChange={(e) => handleInputChange('targetWordCount', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 50000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Chapters
                </label>
                <input
                  type="number"
                  value={formData.estimatedChapters}
                  onChange={(e) => handleInputChange('estimatedChapters', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Book Type
              </label>
              <select
                value={formData.bookType}
                onChange={(e) => handleInputChange('bookType', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="standalone">Standalone</option>
                <option value="series">Part of Series</option>
              </select>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ideal Start Date
                </label>
                <input
                  type="date"
                  value={formData.idealStartDate}
                  onChange={(e) => handleInputChange('idealStartDate', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ideal Finish Date
                </label>
                <input
                  type="date"
                  value={formData.idealFinishDate}
                  onChange={(e) => handleInputChange('idealFinishDate', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Known Constraints
              </label>
              <textarea
                value={formData.constraints}
                onChange={(e) => handleInputChange('constraints', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Holidays, busy seasons, other commitments..."
              />
            </div>
          </div>
        </div>

        {/* Writing Targets */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Writing Targets</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sessions Per Week
                </label>
                <input
                  type="number"
                  value={formData.sessionsPerWeek}
                  onChange={(e) => handleInputChange('sessionsPerWeek', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 3"
                  min="1"
                  max="7"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Words Per Session
                </label>
                <input
                  type="number"
                  value={formData.wordsPerSession}
                  onChange={(e) => handleInputChange('wordsPerSession', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Writing Days
              </label>
              <div className="flex flex-wrap gap-2">
                {days.map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formData.preferredDays.includes(day)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Grace Margin */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grace Margin</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Planned Buffer Weeks
              </label>
              <input
                type="number"
                value={formData.bufferWeeks}
                onChange={(e) => handleInputChange('bufferWeeks', e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 2"
                min="0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personal Notes on Flexibility
              </label>
              <textarea
                value={formData.flexibilityNotes}
                onChange={(e) => handleInputChange('flexibilityNotes', e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="How will you show yourself grace when life happens?"
              />
            </div>
          </div>
        </div>

        {/* Planning Summary */}
        {canComplete && (
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Planning Summary</h3>
            <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
              <p><strong>Goal:</strong> {formData.targetWordCount} words in {formData.estimatedChapters} chapters</p>
              <p><strong>Timeline:</strong> {formData.idealStartDate} to {formData.idealFinishDate}</p>
              <p><strong>Writing Schedule:</strong> {formData.sessionsPerWeek} sessions per week, {formData.wordsPerSession} words per session</p>
              <p><strong>Preferred Days:</strong> {formData.preferredDays.join(', ') || 'Not specified'}</p>
              {formData.bufferWeeks && <p><strong>Buffer:</strong> {formData.bufferWeeks} weeks</p>}
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleThree;